import React, { FC } from 'react'
import { Checkbox } from '@material-ui/core'
import styled from 'styled-components'

import { DetailsContent } from 'src/shared/styled/Layout'
import { IUser } from 'src/models/user'
import TextField from 'src/shared/views/TextField'
import {
  GENDER_DROPDOWN_ITEMS,
  PROVINCES_DROPDOWN_VALUES,
  TIMEZONE_DROPDOWN_ITEMS
} from 'src/constants/user'
import { DropDown, IDropDownItem } from 'src/shared/views/DropDown'
import { COLORS, FONTS } from 'src/shared/theme'
import UserNotesContainer from '../containers/UserNotes'
import { Heading } from 'src/shared/styled/Text'
import { ExitToApp, FileCopyOutlined } from '@material-ui/icons'
import { Link } from 'react-router-dom'
import { useUserBalances } from 'src/shared/hooks/users'
import LoadingScreen from 'src/shared/views/LoadingScreen'

interface IUserInfoViewAccount {
  phoneNumberDropDownValues: IDropDownItem[]
  user: IUser
}

const formFieldStyles = {
  marginBottom: '30px',
  marginRight: '20px',
  minWidth: '200px',
  width: '50%',
  maxWidth: '250px'
}

const StyledDivider = styled.hr`
  border-width: 0;
  border-bottom-width: 1px;
  border-bottom-color: ${COLORS.alto};
`

const BoldText = styled.span`
  font-weight: bold;
  font-family: ${FONTS.medium};
  font-size: 16px;
`

const EarningsContainer = styled.div`
  margin: 30px;
`

const EarningsHeaderButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const EarningsHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const FormFieldsRow = styled.span``

const FormDropDown = styled(DropDown)`
  margin-right: 40px;
`

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`

const formatDob = (dob: string) => {
  if (!dob) return ''
  // Date inputs expect YYYY-MM-DD
  return dob.slice(0, 10)
}

const UserInfoAccount: FC<IUserInfoViewAccount> = ({
  phoneNumberDropDownValues,
  user
}) => {
  const { data, isLoading } = useUserBalances(user.userId)

  const userBalances = React.useMemo(() => {
    if (!data || isLoading) {
      return <LoadingScreen />
    }

    return (
      <div>
        <p>
          <BoldText>Lifetime Earnings:</BoldText> {data.lifetimeEarnings.amount}{' '}
          {data.lifetimeEarnings.currencyCode}
        </p>
        <p>
          <BoldText>Lifetime Payouts:</BoldText> {data.lifetimePayouts.amount}{' '}
          {data.lifetimePayouts.currencyCode}
        </p>
        <p>
          <BoldText>Total Liability:</BoldText> {data.totalLiability.amount}{' '}
          {data.totalLiability.currencyCode}
        </p>
      </div>
    )
  }, [data, isLoading])

  return (
    <>
      <DetailsContent>
        <div style={{ marginBottom: '30px' }}>
          <Checkbox
            disabled
            id="physicalCardRequested"
            checked={!!user.physicalCardRequested}
            color="primary"
          />
          <label htmlFor="physicalCardRequested">
            Physical Blue Charm card requested
          </label>
        </div>
        <FormFieldsRow>
          <TextField
            style={formFieldStyles}
            label="First Name"
            value={user.firstName || ''}
            disabled
          />
          <TextField
            style={formFieldStyles}
            label="Last Name"
            value={user.lastName || ''}
            disabled
          />
          <TextField
            style={formFieldStyles}
            type="date"
            InputLabelProps={{ shrink: true }}
            label="Date of Birth"
            value={formatDob(user.dob)}
            disabled
          />
          <FormDropDown
            items={GENDER_DROPDOWN_ITEMS}
            label="Sex"
            style={formFieldStyles}
            value={user.gender || ''}
            disabled
          />
          <FormDropDown
            items={TIMEZONE_DROPDOWN_ITEMS}
            label="Time Zone"
            style={formFieldStyles}
            value={user.preferredTimeZoneId || ''}
            disabled
          />
        </FormFieldsRow>
        <FormFieldsRow>
          <TextField
            style={formFieldStyles}
            label="Address"
            value={user.address?.street || ''}
            disabled
          />
          <TextField
            style={formFieldStyles}
            label="Unit"
            value={user.address?.unit || ''}
            disabled
          />
          <TextField
            style={formFieldStyles}
            label="City"
            value={user.address?.city || ''}
            disabled
          />
          <FormDropDown
            items={PROVINCES_DROPDOWN_VALUES}
            label="Province"
            style={formFieldStyles}
            value={user.address?.province || ''}
            disabled
          />
          <TextField
            style={formFieldStyles}
            label="Postal Code"
            value={user.address?.postalCode || ''}
            disabled
          />
        </FormFieldsRow>
        <FormFieldsRow>
          <TextField
            style={formFieldStyles}
            variant="outlined"
            label="Email Address"
            value={user.email || ''}
            disabled
          />
          <FormDropDown
            items={phoneNumberDropDownValues}
            label={`Phone Numbers (${phoneNumberDropDownValues.length})`}
            style={formFieldStyles}
            value={
              user.phoneNumber || phoneNumberDropDownValues[0]?.value || ''
            }
            disabled
            helperText={
              user.phoneNumber ? (
                <span
                  style={{ cursor: 'copy' }}
                  onClick={() =>
                    navigator.clipboard.writeText(user.phoneNumber || '')
                  }
                >
                  Copy
                </span>
              ) : undefined
            }
          />
        </FormFieldsRow>
      </DetailsContent>
      <StyledDivider />
      <Columns>
        <DetailsContent style={{ width: '50%' }}>
          <Heading>Misc Details</Heading>
          <div>
            <BoldText>Referral Code: </BoldText>
            {user.referralCode ? (
              <>
                <span>{user.referralCode}</span>
                <FileCopyOutlined
                  color="primary"
                  style={{ height: '1rem', cursor: 'pointer' }}
                  onClick={() =>
                    navigator.clipboard.writeText(user.referralCode || '')
                  }
                />
              </>
            ) : (
              <span>None</span>
            )}
          </div>
          <div>
            <BoldText>SMS Notifications: </BoldText>
            <span>{user.notifications.sms ? 'Enabled' : 'Disabled'}</span>
          </div>
        </DetailsContent>
        <EarningsContainer style={{ width: '50%' }}>
          <EarningsHeaderButtonContainer>
            <EarningsHeaderContainer>
              <Heading>Earnings</Heading>

              <Link
                target="_blank"
                to={`/tools/balance-reports?userId=${user.userId}`}
              >
                <ExitToApp color="primary" />
              </Link>
            </EarningsHeaderContainer>
          </EarningsHeaderButtonContainer>

          {userBalances}
        </EarningsContainer>
      </Columns>
      <StyledDivider />
      <UserNotesContainer />
    </>
  )
}

export default UserInfoAccount
